<template>
    <div>
        <div class="container">
            <SectionIntro :title="$t(modeConfig.titleKey)" class="mt-16" :subtitle="$t(modeConfig.subtitleKey)" xl />
            <MapProductSwitch />
            <div class="grid grid-cols-12 mt-4 lg:mt-12 lg:gap-x-8">
                <div class="col-span-12 lg:col-span-8 h-full flex flex-col mb-4 lg:mb-0">
                    <div class="lg:flex-1 lg:min-h-0">
                        <div class="editor-preview-viewport block lg:flex lg:flex-col box-shadow-preview rounded-lg">
                            <div class="editor-preview-stage rounded-lg overflow-hidden relative lg:flex-1 lg:min-h-0">
                                <div ref="preview-window" class="absolute inset-0">
                                    <client-only>
                                        <Preview3d
                                            :touch-hint="$t('editor.wood_map.touch_hint')"
                                            class="absolute inset-0"
                                            :width="board.width"
                                            :height="board.height"
                                            :cuts="cuts"
                                            :backplate-engraving="masks.boundary"
                                            :woods="woodColors"
                                            :title="plateTitle"
                                            :subtitle="plateSubtitle"
                                            :title-typo="selectedOptions.title.typo"
                                            :title-size="selectedOptions.title.size || 1"
                                            :exploded="exploded"
                                            :slice-count="3"
                                            :explode-gap="18"
                                            label-on-frame
                                            :pin="pin"
                                        />
                                    </client-only>
                                    <div
                                        v-if="isLoading || errorMessage || !cuts.length"
                                        data-html2canvas-ignore
                                        class="absolute inset-0 flex items-center justify-center pointer-events-none"
                                    >
                                        <span
                                            class="bg-white/90 rounded-full px-4 py-2 text-sm font-Inter-Semibold shadow-md"
                                        >
                                            <i
                                                v-if="isLoading"
                                                class="ri-loader-4-line animate-spin inline-block mr-1"
                                            ></i>
                                            {{ $t(errorMessage || 'editor.topo_map.loading_layers') }}
                                        </span>
                                    </div>
                                    <div
                                        v-if="false && masks.parts"
                                        data-html2canvas-ignore
                                        class="absolute bottom-4 right-4 z-10 rounded-full bg-white/95 shadow-md px-4 py-2 text-sm font-Inter-Semibold"
                                    >
                                        {{
                                            $t('editor.topo_map.parts_hint', {
                                                n: masks.parts,
                                                land: masks.landParts,
                                                water: masks.waterParts,
                                            })
                                        }}
                                    </div>
                                </div>
                            </div>
                            <button
                                type="button"
                                data-html2canvas-ignore
                                class="z-10 mx-auto mt-3 mb-3 flex w-max items-center gap-1 rounded-full bg-white/95 px-4 py-2 text-sm font-Inter-Semibold shadow-md lg:absolute lg:bottom-4 lg:left-4 lg:mx-0 lg:my-0"
                                :aria-pressed="exploded"
                                @click="exploded = !exploded"
                            >
                                <i :class="exploded ? 'ri-contract-up-down-line' : 'ri-stack-line'"></i>
                                {{
                                    exploded
                                        ? $t('editor.wood_map.assembled_view')
                                        : $t('editor.wood_map.explode_layers')
                                }}
                            </button>
                        </div>
                    </div>
                    <p class="hidden lg:block text-xs opacity-60 mt-2 mb-2 text-center shrink-0">
                        {{ $t('editor.wood_map.rotate_hint') }}
                        <span v-if="layerHint" class="block mt-1">{{ layerHint }}</span>
                    </p>
                </div>
                <div class="col-span-12 lg:col-span-4 relative">
                    <div ref="scrollTargetTop"></div>
                    <h2
                        class="font-Inter-Bold text-lg sm:text-xl mb-4 sm:mb-8 text-left border-b border-black border-dashed border-opacity-20 block pb-4"
                    >
                        {{ $t('editor.customize') }}
                    </h2>
                    <EditorFields>
                        <div class="input-outer-wrapper">
                            <label
                                for="topoSearchTextField"
                                class="text-sm font-Inter-Bold text-grey-900 input-label inline-flex"
                            >
                                {{ $t('editor.location.label') }}
                            </label>
                            <div class="flex items-stretch gap-2">
                                <div class="relative flex-1 min-w-0">
                                    <input
                                        id="topoSearchTextField"
                                        ref="locationInput"
                                        v-model="selectedOptions.locationQuery"
                                        type="text"
                                        autocomplete="off"
                                        role="combobox"
                                        :aria-expanded="suggestions.length > 0"
                                        aria-autocomplete="list"
                                        aria-controls="topo-location-list"
                                        :placeholder="$t('editor.location.placeholder')"
                                        class="p-inputtext p-component w-full"
                                        @input="debouncedSearch"
                                        @keydown.enter.prevent="selectFirstSuggestion"
                                        @keydown.esc="suggestions = []"
                                        @blur="suggestions = []"
                                    />
                                    <!-- in body: the mobile swipe row clips overflow, so an in-place list would be cut off -->
                                    <portal selector="body">
                                        <ul
                                            v-if="suggestions.length"
                                            id="topo-location-list"
                                            role="listbox"
                                            class="absolute z-50 mt-1 bg-white rounded-lg shadow-md ring-1 ring-black/10 overflow-hidden"
                                            :style="suggestionStyle"
                                        >
                                            <li
                                                v-for="feature in suggestions"
                                                :key="feature.id"
                                                role="option"
                                                class="px-3 py-2 text-sm cursor-pointer hover:bg-black/5"
                                                @mousedown.prevent="selectPlace(feature)"
                                            >
                                                {{ feature.place_name }}
                                            </li>
                                        </ul>
                                    </portal>
                                </div>
                                <button
                                    type="button"
                                    class="p-inputtext p-component shrink-0 self-stretch px-0 flex items-center justify-center"
                                    style="aspect-ratio: 1; width: auto"
                                    :class="locatorOpen ? 'bg-black/5' : ''"
                                    :aria-expanded="locatorOpen"
                                    aria-controls="topo-locator"
                                    :aria-label="$t('editor.wood_map.adjust_map')"
                                    @click="toggleLocator"
                                >
                                    <i class="text-lg" :class="locatorOpen ? 'ri-close-line' : 'ri-map-2-line'"></i>
                                </button>
                            </div>
                            <LocationDialog :open="locatorOpen" @close="locatorOpen = false">
                                <div v-show="locatorOpen" id="topo-locator">
                                    <span class="text-xs opacity-60 block mb-2">{{
                                        $t('editor.wood_map.locator_hint')
                                    }}</span>
                                    <div
                                        ref="locator"
                                        class="w-full rounded-lg overflow-hidden relative bg-[#d7c4a4]"
                                        :class="dragging ? 'cursor-grabbing' : 'cursor-grab'"
                                        :style="{
                                            aspectRatio: `${board.width} / ${board.height}`,
                                            touchAction: 'none',
                                        }"
                                        @pointerdown="onPanStart"
                                        @pointermove="onPanMove"
                                        @pointerup="onPanEnd"
                                        @pointercancel="onPanEnd"
                                        @lostpointercapture="onPanEnd"
                                        @wheel.prevent="onWheel"
                                    >
                                        <canvas
                                            ref="locatorCanvas"
                                            class="absolute inset-0 w-full h-full pointer-events-none"
                                            :style="{ transform: `translate(${panX}px, ${panY}px)` }"
                                        />
                                        <div
                                            class="absolute top-2 right-2 flex flex-col rounded-md overflow-hidden bg-white/95 shadow-md ring-1 ring-black/5"
                                        >
                                            <button
                                                type="button"
                                                class="flex h-8 w-8 items-center justify-center hover:bg-black/5 disabled:opacity-40"
                                                :disabled="zoomLevel >= modeConfig.maxZoom"
                                                :aria-label="$t('editor.map.zoom_in')"
                                                @click="nudgeZoom(1)"
                                                @pointerdown.stop
                                            >
                                                <i class="ri-add-line"></i>
                                            </button>
                                            <button
                                                type="button"
                                                class="flex h-8 w-8 items-center justify-center hover:bg-black/5 disabled:opacity-40"
                                                :disabled="zoomLevel <= modeConfig.minZoom"
                                                :aria-label="$t('editor.map.zoom_out')"
                                                @click="nudgeZoom(-1)"
                                                @pointerdown.stop
                                            >
                                                <i class="ri-subtract-line"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </LocationDialog>
                            <div class="editor-label-row mt-4 !mb-0 flex justify-between w-full">
                                <label
                                    for="topo-engrave-boundary"
                                    class="text-sm font-Inter-Bold text-grey-900 input-label cursor-pointer"
                                >
                                    {{ $t('editor.topo_map.engrave_boundary') }}
                                </label>
                                <InputSwitch
                                    v-model="selectedOptions.engraveBoundary"
                                    input-id="topo-engrave-boundary"
                                />
                            </div>
                            <div class="editor-label-row mt-4 !mb-0 flex justify-between w-full">
                                <label
                                    for="topo-show-pin"
                                    class="text-sm font-Inter-Bold text-grey-900 input-label cursor-pointer"
                                >
                                    {{ $t('editor.wood_map.pin_label') }}
                                </label>
                                <InputSwitch v-model="selectedOptions.showPin" input-id="topo-show-pin" />
                            </div>
                            <Input
                                v-if="selectedOptions.showPin"
                                v-model="selectedOptions.pinText"
                                class="w-full mt-4"
                                :label="$t('editor.message.label')"
                                :placeholder="$t('editor.wood_map.pin_placeholder')"
                                :required="false"
                                :maxlength="16"
                            />
                        </div>
                        <div v-if="editorType === 'topo-map'" class="input-outer-wrapper">
                            <Input
                                v-model="selectedOptions.mode"
                                class="w-full"
                                :placeholder="$t('editor.topo_map.mode_label')"
                                :options="modeOptions"
                                is-dropdown
                                :label="$t('editor.topo_map.mode_label')"
                                :show="onDropdownShow"
                                :hide="onDropdownHide"
                            />
                        </div>
                        <div class="input-outer-wrapper">
                            <Input
                                v-model="selectedOptions.layerCount"
                                class="w-full"
                                :placeholder="$t('editor.topo_map.layers_label')"
                                :options="layerCountOptions"
                                is-dropdown
                                :label="$t('editor.topo_map.layers_label')"
                                :show="onDropdownShow"
                                :hide="onDropdownHide"
                            />
                        </div>
                        <div v-if="modeId !== 'city'" class="input-outer-wrapper">
                            <Input
                                v-model="selectedOptions.waterStyle"
                                class="w-full"
                                :placeholder="$t('editor.topo_map.water_style')"
                                :options="waterStyleOptions"
                                is-dropdown
                                :label="$t('editor.topo_map.water_style')"
                                :show="onDropdownShow"
                                :hide="onDropdownHide"
                            />
                        </div>
                        <div v-for="layer in layers" :key="layer.key" class="input-outer-wrapper">
                            <span
                                class="text-sm font-Inter-Bold text-grey-900 input-label inline-flex w-full justify-center text-center lg:justify-start lg:text-left"
                            >
                                {{ $t(layer.labelKey) }}:
                                <span class="font-Inter-Regular">{{
                                    $t(woodLabelKey(selectedOptions[layer.key]?.id))
                                }}</span>
                            </span>
                            <div
                                class="flex flex-wrap justify-center gap-3 lg:justify-start"
                                role="radiogroup"
                                :aria-label="$t(layer.labelKey)"
                            >
                                <button
                                    v-for="wood in layer.options"
                                    :key="wood.id"
                                    type="button"
                                    role="radio"
                                    :aria-checked="selectedOptions[layer.key]?.id === wood.id"
                                    :aria-label="$t(wood.labelKey)"
                                    :title="$t(wood.labelKey)"
                                    class="w-9 h-9 rounded-full ring-offset-2 transition"
                                    :class="
                                        selectedOptions[layer.key]?.id === wood.id
                                            ? 'ring-2 ring-black'
                                            : 'border border-black border-opacity-20'
                                    "
                                    :style="{ backgroundColor: wood.color }"
                                    @click="selectedOptions[layer.key] = { id: wood.id, name: wood.name }"
                                ></button>
                            </div>
                        </div>
                        <div class="input-outer-wrapper">
                            <Input
                                v-model="selectedOptions.size.id"
                                class="w-full"
                                :placeholder="$t('editor.size.placeholder')"
                                :options="sizeOptions"
                                is-dropdown
                                :label="$t('editor.size.label')"
                                :show="onDropdownShow"
                                :hide="onDropdownHide"
                            />
                        </div>
                        <div class="input-outer-wrapper">
                            <Input
                                v-model="selectedOptions.title.value"
                                class="w-full"
                                :label="$t('editor.wood_map.plate_label')"
                                :placeholder="$t('editor.wood_map.plate_placeholder')"
                                :required="false"
                                :maxlength="24"
                            >
                                <template #label-end>
                                    <div class="flex items-center gap-3">
                                        <TextSizeToggle
                                            :value="selectedOptions.title.size || 1"
                                            :label="$t('editor.wood_map.plate_label')"
                                            @input="setTextSize('title', $event)"
                                        />
                                        <InputSwitch
                                            v-model="selectedOptions.showPlate"
                                            input-id="topo-show-plate"
                                            :aria-label="$t('editor.wood_map.plate_label')"
                                        />
                                    </div>
                                </template>
                            </Input>
                        </div>
                        <FontFamilySelector
                            :model-value="selectedOptions.title.typo"
                            :label="$t('editor.wood_map.plate_font')"
                            label-class="lg:text-left"
                            @update:modelValue="setTypo('title', $event)"
                            @show="onDropdownShow"
                            @hide="onDropdownHide"
                        />
                    </EditorFields>
                    <!-- on mobile the hint sits under the form; the preview shows the touch overlay instead -->
                    <p class="lg:hidden text-xs opacity-60 mt-1 md:mt-4 text-center">
                        {{ $t('editor.wood_map.touch_hint') }}
                        <span v-if="layerHint" class="block mt-1">{{ layerHint }}</span>
                    </p>
                    <div class="w-full mt-4 lg:mb-4">
                        <Input
                            v-model="selectedOptions.comment"
                            is-textarea
                            :label="$t('editor.comment.label')"
                            :rows="3"
                            :placeholder="$t('editor.comment.placeholder')"
                            :required="false"
                        />
                    </div>
                    <p class="text-xs opacity-60 mt-2 mb-4">
                        {{ $t('editor.wood_map.map_data') }} ©
                        <a href="https://www.mapbox.com/about/maps" target="_blank" rel="noopener" class="underline"
                            >Mapbox</a
                        >
                        ©
                        <a
                            href="https://www.openstreetmap.org/copyright"
                            target="_blank"
                            rel="noopener"
                            class="underline"
                            >OpenStreetMap</a
                        >
                    </p>
                    <StickyPriceCard
                        :quantity="quantity"
                        :name="$t(modeConfig.titleKey)"
                        :price="price"
                        :image="previewImage"
                        :before-add-to-cart="capturePreview"
                        :slug="localePath({ route: getRouteBaseName(), hash: `#${hash || randomId}` })"
                        :product-id="productId"
                        :options="mutatedOptions"
                        :is-preview-capturing="isPreviewCapturing"
                        :is-dropdown-open="isDropdownOpen"
                        :scroll-target-bottom="$refs.scrollTargetBottom"
                        :scroll-target-top="$refs.scrollTargetTop"
                        :extras="cartExtras"
                    />
                    <div ref="scrollTargetBottom"></div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { debounce } from 'vue-debounce';
import StickyPriceCard from '@/components/Cards/StickyPriceCard/StickyPriceCard.vue';
import MapProductSwitch from '@/components/Editor/Shared/MapProductSwitch.vue';
import editorMixin from '@/components/Editor/Shared/editorMixin';
import { SIZES } from '@/components/Editor/Shared/productOptions';
import EditorFields from '@/components/Editor/Shared/EditorFields.vue';
import LocationDialog from '@/components/Editor/Shared/LocationDialog.vue';
import Preview3d from '@/components/Editor/WoodMap/Preview3d.vue';
import FontFamilySelector from '@/components/UI/FontFamilySelector/FontFamilySelector.vue';
import TextSizeToggle from '@/components/Editor/Shared/TextSizeToggle.vue';
import { LAYER_COUNTS, TOPO_LAYERS, TOPO_MODES, WATER_STYLES, topoWoodById } from '@/components/Editor/TopoMap/options';
import {
    drawCityLayers,
    drawLocatorCity,
    drawLocatorTopo,
    drawTopoLayers,
    loadCity,
    loadTerrain,
    traceRings,
} from '@/components/Editor/TopoMap/terrain';
import {
    TILE_SIZE,
    finestPlaceType,
    fromWorld,
    geocodePlaces,
    loadPlaceBoundary,
    toWorld,
    viewFromCenter,
} from '@/components/Editor/WoodMap/mapTiles';

// half steps: a full zoom doubles the area, too coarse to frame a region
const ZOOM_STEP = 0.5;
const VIEW_WIDTH = 400;

function shadeHex(hex, amount) {
    const n = parseInt(hex.slice(1), 16);
    const mix = (channel) => Math.round(channel * (1 - amount));
    const r = mix(n >> 16);
    const g = mix((n >> 8) & 255);
    const b = mix(n & 255);
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

export default {
    name: 'TopoMapEditor',
    components: {
        StickyPriceCard,
        MapProductSwitch,
        Preview3d,
        EditorFields,
        LocationDialog,
        FontFamilySelector,
        TextSizeToggle,
    },
    mixins: [editorMixin],
    data() {
        return {
            // shortcut: placeholder price and the poster map's product id, set the topographic map's own before launch
            basePrice: 29900,
            productId: 125,
            editorType: 'topo-map',
            layers: TOPO_LAYERS,
            mapCenter: { lat: -36.82, lng: 174.78 },
            zoomLevel: 11,
            locatorSize: { width: 0, height: 0 },
            panX: 0,
            panY: 0,
            suggestions: [],
            suggestionStyle: null,
            dragging: false,
            terrain: null,
            boundaryRings: [],
            masks: {
                land: [],
                landEngraving: [],
                water: [],
                waterEngraving: [],
                boundary: null,
                interval: 0,
                flat: false,
                parts: 0,
                landParts: 0,
                waterParts: 0,
            },
            isLoading: false,
            errorMessage: null,
            exploded: false,
            locatorOpen: false,
            selectedOptions: {
                size: { id: 1, name: SIZES[0].name, price: 0 },
                frame: { id: null },
                landWood: { id: 1, name: 'Nyír' },
                waterWood: { id: 8, name: 'Azúr' },
                frameWood: { id: 1, name: 'Nyír' },
                layerCount: 10,
                waterStyle: 'flat',
                mode: 'terrain',
                title: { value: 'Auckland', size: 1, typo: null },
                showPlate: true,
                // the pin marks the map centre, i.e. the searched place
                showPin: false,
                pinText: '',
                comment: null,
                preview: null,
                hasDigital: { active: false, price: 0 },
                locationLatLong: '-36.82, 174.78',
                zoomLevel: 11,
                locationQuery: '',
                locationKind: null,
                engraveBoundary: false,
            },
        };
    },
    computed: {
        board() {
            const size = SIZES.find((option) => option.id === this.selectedOptions.size.id) || SIZES[0];
            return { width: size.width * 10, height: size.height * 10 };
        },
        woodColors() {
            const color = (id) => topoWoodById(id).color;
            return {
                top: color(this.selectedOptions.landWood?.id),
                middle: color(this.selectedOptions.landWood?.id),
                backplate: color(this.selectedOptions.waterWood?.id),
                frame: color(this.selectedOptions.frameWood?.id),
            };
        },
        cuts() {
            const landColor = this.woodColors.top;
            const waterColor = this.woodColors.backplate;
            const waterCuts = (this.masks.water || []).map((mask, index, all) => ({
                mask,
                color: shadeHex(waterColor, ((all.length - 1 - index) / Math.max(all.length, 1)) * 0.28),
                engraving: this.masks.waterEngraving[index],
                edge: 'broad',
                z: index,
            }));
            const lift = this.masks.landLift || 0;
            const land = this.masks.land || [];
            const fillers = land.length
                ? Array.from({ length: lift }, (_, index) => ({ mask: land[0], color: landColor, edge: 'broad', z: index }))
                : [];
            const landCuts = land.map((mask, index) => ({
                mask,
                color: landColor,
                engraving: this.masks.landEngraving[index],
                edge: 'broad',
                z: index + lift,
            }));
            return [...waterCuts, ...fillers, ...landCuts];
        },
        coordinates() {
            const [lat, lng] = this.selectedOptions.locationLatLong.split(',').map(parseFloat);
            if (isNaN(lat) || isNaN(lng)) return '';
            const ns = this.$t(lat >= 0 ? 'editor.wood_map.north' : 'editor.wood_map.south');
            const ew = this.$t(lng >= 0 ? 'editor.wood_map.east' : 'editor.wood_map.west');
            return `${Math.abs(lat).toFixed(4)}° ${ns}, ${Math.abs(lng).toFixed(4)}° ${ew}`;
        },
        plateTitle() {
            return this.selectedOptions.showPlate === false ? '' : this.selectedOptions.title.value;
        },
        pin() {
            return this.selectedOptions.showPin ? { text: this.selectedOptions.pinText || '' } : null;
        },
        plateSubtitle() {
            return this.selectedOptions.showPlate === false ? '' : this.coordinates;
        },
        waterStyleId() {
            const style = this.selectedOptions.waterStyle;
            return style && style.id ? style.id : style;
        },
        modeId() {
            const mode = this.selectedOptions.mode;
            return (mode && mode.id ? mode.id : mode) || 'terrain';
        },
        modeConfig() {
            return TOPO_MODES.find((mode) => mode.id === this.modeId) || TOPO_MODES[0];
        },
        modeOptions() {
            return TOPO_MODES.map((mode) => ({ id: mode.id, name: this.$t(mode.labelKey) }));
        },
        layerCountId() {
            const count = this.selectedOptions.layerCount;
            return count && count.id ? count.id : count;
        },
        viewSize() {
            return { width: VIEW_WIDTH, height: Math.round((VIEW_WIDTH * this.board.height) / this.board.width) };
        },
        previewView() {
            const { width, height } = this.viewSize;
            return viewFromCenter(this.mapCenter.lat, this.mapCenter.lng, this.zoomLevel, width, height);
        },
        layerCountOptions() {
            return LAYER_COUNTS.map((count) => ({
                id: count,
                name: this.$t('editor.topo_map.layers_n', { n: count }),
            }));
        },
        waterStyleOptions() {
            return WATER_STYLES.map((style) => ({
                id: style.id,
                name: this.$t(style.labelKey),
            }));
        },
        layerHint() {
            if (!this.terrain || this.isLoading) return '';
            if (this.modeId === 'city') {
                if (this.masks.flat) return this.$t('editor.topo_map.city_empty_hint');
                return this.$t('editor.topo_map.city_hint', {
                    max: Math.round(this.masks.maxHeight),
                    interval: Math.round(this.masks.interval),
                });
            }
            if (this.masks.flat) return this.$t('editor.topo_map.flat_hint');
            return this.$t('editor.topo_map.elevation_hint', {
                min: Math.round(this.terrain.minLand),
                max: Math.round(this.terrain.maxLand),
                interval: Math.round(this.masks.interval),
            });
        },
    },
    watch: {
        selectedOptions: {
            handler() {
                this.debouncedCheckOptions(this.selectedOptions);
            },
            deep: true,
        },
        'selectedOptions.layerCount': 'rebuildMasks',
        'selectedOptions.waterStyle': 'rebuildMasks',
        'selectedOptions.engraveBoundary'(on) {
            if (on && !this.boundaryRings.length) this.loadBoundary(this.boundaryFeature());
            else this.rebuildMasks();
        },
        'selectedOptions.size.id': 'loadMap',
        modeId(mode, previous) {
            if (mode === previous) return;
            this.terrain = null;
            this.zoomLevel = this.clampZoom(this.zoomLevel);
            this.syncView();
        },
    },
    created() {
        this.debouncedLoadMap = debounce(this.loadMap, 300);
        this.debouncedSearch = debounce(this.searchPlaces, 300);
    },
    mounted() {
        const saved = this.loadSavedProduct();
        if (saved) {
            this.selectedOptions = { ...this.selectedOptions, ...this.$cloneDeep(saved) };
            const [lat, lng] = (saved.locationLatLong || '').split(',').map(parseFloat);
            if (!isNaN(lat) && !isNaN(lng)) this.mapCenter = { lat, lng };
            if (saved.zoomLevel) {
                this.zoomLevel = this.clampZoom(saved.zoomLevel);
            }
        }
        this.checkOptions(this.selectedOptions);
        if (this.selectedOptions.showPlate == null) this.selectedOptions.showPlate = true;
        if (this.selectedOptions.engraveBoundary == null) this.selectedOptions.engraveBoundary = false;
        this.$nextTick(() => {
            this.observeLocator();
            this.loadMap();
            if (this.selectedOptions.engraveBoundary) this.loadBoundary(this.boundaryFeature());
        });
    },
    beforeDestroy() {
        if (this.locatorObserver) this.locatorObserver.disconnect();
    },
    methods: {
        // into the mode's range, on a half step
        clampZoom(zoom) {
            const { minZoom, maxZoom } = this.modeConfig;
            return Math.round(Math.min(maxZoom, Math.max(minZoom, zoom)) / ZOOM_STEP) * ZOOM_STEP;
        },
        woodLabelKey(id) {
            return topoWoodById(id).labelKey;
        },
        toggleLocator() {
            this.locatorOpen = !this.locatorOpen;
            if (!this.locatorOpen) return;
            // a macrotask, not $nextTick: on mobile the locator renders through the modal's portal a few ticks later
            setTimeout(() => {
                this.observeLocator();
                this.measureLocator();
                this.paintLocator();
            });
        },
        // the locator is remounted when the mobile modal opens, so the observer follows it
        observeLocator() {
            if (this.locatorObserver) this.locatorObserver.disconnect();
            if (!this.$refs.locator) return;
            this.locatorObserver = new ResizeObserver(() => {
                this.measureLocator();
                if (this.locatorOpen) this.paintLocator();
            });
            this.locatorObserver.observe(this.$refs.locator);
        },
        measureLocator() {
            const el = this.$refs.locator;
            if (!el) return;
            this.locatorSize = { width: el.clientWidth, height: el.clientHeight };
        },
        syncView() {
            this.selectedOptions.locationLatLong = `${this.mapCenter.lat}, ${this.mapCenter.lng}`;
            this.selectedOptions.zoomLevel = this.zoomLevel;
            this.debouncedLoadMap();
        },
        onPanStart(event) {
            if (event.button) return;
            this.suggestions = [];
            this.dragging = true;
            this.dragStart = { x: event.clientX, y: event.clientY, ...this.mapCenter };
            event.currentTarget.setPointerCapture(event.pointerId);
        },
        onPanMove(event) {
            if (!this.dragging) return;
            this.panX = event.clientX - this.dragStart.x;
            this.panY = event.clientY - this.dragStart.y;
            const worldPerPx = 1 / (TILE_SIZE * 2 ** this.zoomLevel);
            const [cx, cy] = toWorld(this.dragStart.lat, this.dragStart.lng);
            this.mapCenter = fromWorld(cx - this.panX * worldPerPx, cy - this.panY * worldPerPx);
        },
        paintLocator() {
            if (!this.locatorOpen || !this.terrain) return;
            if (!this.locatorSize.width) this.measureLocator();
            const canvas = this.$refs.locatorCanvas;
            if (!canvas || !this.locatorSize.width) return;
            const dpr = window.devicePixelRatio || 1;
            canvas.width = this.locatorSize.width * dpr;
            canvas.height = this.locatorSize.height * dpr;
            if (this.terrain.buildings) drawLocatorCity(canvas, this.terrain);
            else drawLocatorTopo(canvas, this.terrain);
            if (this.selectedOptions.engraveBoundary) {
                const ctx = canvas.getContext('2d');
                ctx.strokeStyle = 'rgba(42, 24, 12, 0.9)';
                traceRings(ctx, this.boundaryRings, this.terrain.view);
            }
            this.panX = 0;
            this.panY = 0;
        },
        onPanEnd() {
            if (!this.dragging) return;
            this.dragging = false;
            this.syncView();
        },
        onWheel(event) {
            const now = Date.now();
            if (now - (this.lastWheel || 0) < 120) return;
            this.lastWheel = now;
            this.nudgeZoom(event.deltaY > 0 ? -1 : 1);
        },
        nudgeZoom(delta) {
            const next = this.clampZoom(this.zoomLevel + delta * ZOOM_STEP);
            if (next === this.zoomLevel) return;
            this.zoomLevel = next;
            this.syncView();
        },
        async searchPlaces() {
            const query = (this.selectedOptions.locationQuery || '').trim();
            if (query.length < 2) {
                this.suggestions = [];
                return;
            }
            try {
                this.suggestions = await geocodePlaces(query, this.$config.mapboxToken, {
                    language: this.$i18n.locale,
                    proximity: this.mapCenter,
                    types: this.modeConfig.placeTypes,
                });
                const rect = this.$refs.locationInput.getBoundingClientRect();
                this.suggestionStyle = {
                    top: `${rect.bottom + window.scrollY}px`,
                    left: `${rect.left + window.scrollX}px`,
                    width: `${rect.width}px`,
                };
            } catch (error) {
                console.error('Error searching places:', error);
                this.suggestions = [];
            }
        },
        selectFirstSuggestion() {
            if (this.suggestions[0]) this.selectPlace(this.suggestions[0]);
        },
        boundaryFeature() {
            const name = (this.selectedOptions.locationQuery || this.selectedOptions.title.value || '').trim();
            return {
                place_name: name,
                center: [this.mapCenter.lng, this.mapCenter.lat],
                place_type: this.selectedOptions.locationKind ? [this.selectedOptions.locationKind] : [],
            };
        },
        async loadBoundary(feature, fit = false) {
            const request = (this.boundaryRequest = {});
            try {
                const rings = await loadPlaceBoundary(feature);
                if (this.boundaryRequest !== request) return;
                this.boundaryRings = rings;
                const points = rings.flat();
                if (fit && points.length) {
                    // the outline is tighter than the geocoder's bbox
                    const xs = points.map(([x]) => x);
                    const ys = points.map(([, y]) => y);
                    this.fitView({
                        left: Math.min(...xs),
                        top: Math.min(...ys),
                        right: Math.max(...xs),
                        bottom: Math.max(...ys),
                    });
                    this.syncView();
                }
                this.rebuildMasks();
            } catch (error) {
                if (this.boundaryRequest !== request) return;
                console.error('Error loading place boundary:', error);
                this.boundaryRings = [];
                this.rebuildMasks();
            }
        },
        selectPlace(feature) {
            const [lng, lat] = feature.center;
            this.mapCenter = { lat, lng };
            this.selectedOptions.locationQuery = feature.text;
            this.selectedOptions.locationKind = finestPlaceType(feature.place_type);
            this.selectedOptions.title.value = feature.text.slice(0, 24);
            this.suggestions = [];
            this.$refs.locationInput.blur(); // hides the keyboard and lets the preview scroll into view
            this.boundaryRings = [];
            this.boundaryRequest = {};
            if (this.selectedOptions.engraveBoundary) this.loadBoundary(feature, true);
            if (feature.bbox) {
                const [minLng, minLat, maxLng, maxLat] = feature.bbox;
                const [left, top] = toWorld(maxLat, minLng);
                const [right, bottom] = toWorld(minLat, maxLng);
                this.fitView({ left, top, right, bottom });
            }
            this.syncView();
        },
        // centre the box on the board at the closest half step that still shows all of it
        fitView({ left, top, right, bottom }) {
            const { width, height } = this.viewSize;
            const pad = 1.1;
            const zoom = Math.log2(
                Math.min(width / (TILE_SIZE * pad * (right - left)), height / (TILE_SIZE * pad * (bottom - top)))
            );
            const { minZoom, maxZoom } = this.modeConfig;
            this.zoomLevel = Math.floor(Math.min(maxZoom, Math.max(minZoom, zoom)) / ZOOM_STEP) * ZOOM_STEP;
            this.mapCenter = fromWorld((left + right) / 2, (top + bottom) / 2);
        },
        rebuildMasks() {
            if (!this.terrain) return;
            const draw = this.terrain.buildings ? drawCityLayers : drawTopoLayers;
            this.masks = draw(this.terrain, {
                landLayers: this.layerCountId || 10,
                waterStyle: this.waterStyleId,
                boundary: this.selectedOptions.engraveBoundary ? this.boundaryRings : [],
            });
            this.$nextTick(() => this.paintLocator());
        },
        async loadMap() {
            const view = this.previewView;
            const load = (this.currentLoad = {});
            this.isLoading = true;
            this.errorMessage = null;
            try {
                const loader = this.modeId === 'city' ? loadCity : loadTerrain;
                const terrain = await loader(
                    view,
                    this.zoomLevel,
                    this.$config.mapboxToken,
                    this.board.width / this.board.height
                );
                if (this.currentLoad !== load) return;
                this.terrain = terrain;
                this.rebuildMasks();
            } catch (error) {
                if (this.currentLoad !== load) return;
                console.error('Error loading terrain:', error);
                this.errorMessage = 'editor.topo_map.load_error';
            } finally {
                if (this.currentLoad === load) this.isLoading = false;
            }
        },
    },
};
</script>
